import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ShoppingBag } from "lucide-react";
import { SearchBar, TableFlat } from "@veorest/ui";
import { fetchAdminClients, fetchAdminOrders, type AdminOrder } from "@veorest/api-client";
import { PageHeader } from "../components/shell/PageHeader";
import { AdminPager, AdminPanel } from "../components/shell/AdminPanel";
import { EmptyState } from "../components/data/EmptyState";
import { formatDate, formatIqd, statusPillClass } from "../lib/format";
import { orderDetailPath } from "../routes";

const STATUS_FILTERS = [
  { key: "", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "confirmed", label: "Confirmed" },
  { key: "preparing", label: "Preparing" },
  { key: "ready", label: "Ready" },
  { key: "out_for_delivery", label: "Out for delivery" },
  { key: "delivered", label: "Delivered" },
  { key: "cancelled", label: "Cancelled" },
] as const;

export default function OrdersOverviewPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [page, setPage] = useState(1);

  const clients = useQuery({
    queryKey: ["admin-clients-options"],
    queryFn: () => fetchAdminClients({ limit: 100 }),
  });

  const orders = useQuery({
    queryKey: ["admin-orders", search, status, tenantId, page],
    queryFn: () =>
      fetchAdminOrders({
        search: search || undefined,
        status: status || undefined,
        tenantId: tenantId || undefined,
        page,
        limit: 25,
      }),
  });

  const columns = useMemo(
    () => [
      {
        key: "restaurant",
        header: "Client",
        render: (row: AdminOrder) => (
          <div className="min-w-0">
            <p className="admin-row-title">{row.restaurant_name}</p>
            <p className="admin-row-sub truncate">{row.id}</p>
          </div>
        ),
      },
      {
        key: "customer",
        header: "Customer",
        render: (row: AdminOrder) => (
          <div className="min-w-0">
            <p className="font-medium">{row.customer_name || "—"}</p>
            <p className="admin-row-sub">{row.customer_phone || "—"}</p>
          </div>
        ),
      },
      {
        key: "status",
        header: "Status",
        render: (row: AdminOrder) => (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className={statusPillClass(row.status)}>{row.status}</span>
            {row.return_to_store ? (
              <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-700">
                return to store
              </span>
            ) : null}
          </div>
        ),
      },
      {
        key: "partner",
        header: "Partner",
        render: (row: AdminOrder) => (
          <div className="min-w-0">
            <p className="admin-row-sub truncate">
              {row.partner_name || "—"}
            </p>
            <p className="admin-row-sub">
              {row.partner_declined_at
                ? "declined"
                : row.partner_accepted_at
                  ? "accepted"
                  : row.handling_partner_id
                    ? "awaiting"
                    : "—"}
            </p>
          </div>
        ),
      },
      {
        key: "driver",
        header: "Driver",
        render: (row: AdminOrder) => (
          <span className="text-[var(--text-muted)]">{row.driver_name || "—"}</span>
        ),
      },
      {
        key: "total",
        header: "Total",
        render: (row: AdminOrder) => (
          <span className="admin-money tabular font-bold">
            {formatIqd(row.total)} {row.currency || "IQD"}
          </span>
        ),
      },
      {
        key: "source",
        header: "Source",
        render: (row: AdminOrder) => (
          <span className="text-[var(--text-muted)]">{row.source || row.channel || "—"}</span>
        ),
      },
      {
        key: "created_at",
        header: "Created",
        render: (row: AdminOrder) => (
          <span className="text-[var(--text-muted)]">{formatDate(row.created_at)}</span>
        ),
      },
    ],
    []
  );

  const totalPages = Math.max(1, Math.ceil((orders.data?.total ?? 0) / 25));
  const items = orders.data?.items ?? [];

  return (
    <div className="menu-page">
      <PageHeader
        eyebrow="Platform"
        title="Orders overview"
        subtitle="Cross-tenant order feed for OELOX ops — read-only."
      />

      <AdminPanel
        toolbar={
          <>
            <SearchBar
              value={search}
              onChange={(e) => {
                setPage(1);
                setSearch(e.target.value);
              }}
              placeholder="Search customer, phone, client…"
              className="admin-search"
            />
            <div className="rounded-xl border border-[var(--border-subtle)] bg-[var(--surface-muted)] px-3 py-2 text-sm">
              <label className="block text-[11px] font-semibold uppercase tracking-wide text-[var(--text-muted)] mb-1">
                Client
              </label>
              <select
                className="w-full bg-transparent outline-none font-medium"
                value={tenantId}
                onChange={(e) => {
                  setTenantId(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All clients</option>
                {(clients.data?.items ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="customer-filter-chips admin-filter-chips">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.key || "all"}
                  type="button"
                  className={`customer-filter-chip ${status === f.key ? "customer-filter-chip--active" : ""}`}
                  onClick={() => {
                    setPage(1);
                    setStatus(f.key);
                  }}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </>
        }
      >
        {orders.isLoading ? (
          <div className="space-y-2 p-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-14 rounded-xl skeleton" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<ShoppingBag className="h-5 w-5" />}
            title="No orders found"
            body="Try clearing filters or wait for marketplace / desk orders to appear."
          />
        ) : (
          <div className="admin-table-wrap">
            <TableFlat
              columns={columns}
              data={items}
              keyField="id"
              onRowClick={(row) => navigate(orderDetailPath((row as AdminOrder).id))}
            />
          </div>
        )}
      </AdminPanel>

      <AdminPager
        page={page}
        totalPages={totalPages}
        total={orders.data?.total ?? 0}
        label="orders"
        onPrev={() => setPage((p) => p - 1)}
        onNext={() => setPage((p) => p + 1)}
      />
    </div>
  );
}
